import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Loader2 } from 'lucide-react';

interface AnimatedHumanizingPreviewProps {
  isGenerating: boolean;
  isHumanizing: boolean;
  stealthMode: boolean;
}

const PRESET_MESSAGES = [
  { id: 'scan', text: 'Analyzing context and grading rubrics...', status: 'complete', delay: 0 },
  { id: 'signatures', text: 'Mapping predictable AI syntax & repetitive transitional markers...', status: 'active', delay: 1500 },
  { id: 'burstiness', text: 'Injecting high sentence length variation (structural burstiness)...', status: 'pending', delay: 3200 },
  { id: 'entropy', text: 'Elevating lexical perplexity and semantic density...', status: 'pending', delay: 4800 },
  { id: 'cloning', text: 'Syncing personalized stylistic and vocabulary tier cloning...', status: 'pending', delay: 6500 },
  { id: 'citations', text: 'Verifying claim citations & grounding references...', status: 'pending', delay: 8000 },
];

export const AnimatedHumanizingPreview: React.FC<AnimatedHumanizingPreviewProps> = ({
  isGenerating,
  isHumanizing,
  stealthMode
}) => {
  const [currentProgress, setCurrentProgress] = useState(1);
  const [activeStep, setActiveStep] = useState(0);
  const [scannedPercentage, setScannedPercentage] = useState(24);
  const [logPhase, setLogPhase] = useState(0);
  const [eliminatedWords, setEliminatedWords] = useState<string[]>([]);
  const [addedWords, setAddedWords] = useState<string[]>([]);

  // Simulation of progress bar & calculations
  useEffect(() => {
    const progressInterval = setInterval(() => {
      setCurrentProgress(prev => {
        if (prev >= 100) return 99.4; // cap just before final completion to let the generation stream finish cleanly
        return prev + (100 - prev) * 0.15 + (Math.random() * 2);
      });
    }, 1200);

    const activeStepInterval = setInterval(() => {
      setActiveStep(prev => (prev < PRESET_MESSAGES.length - 1 ? prev + 1 : prev));
    }, 2200);

    const percentageInterval = setInterval(() => {
      setScannedPercentage(prev => {
        if (prev >= 99) return 99;
        return prev + Math.floor(Math.random() * 4) + 1;
      });
    }, 450);

    return () => {
      clearInterval(progressInterval);
      clearInterval(activeStepInterval);
      clearInterval(percentageInterval);
    };
  }, []);

  // Word swapping simulation for "AI word replacement" block
  useEffect(() => {
    const wordsToEliminate = ['moreover', 'testament', 'furthermore', 'it is important to note', 'delve', 'tapestry'];
    const wordsToInject = ['notably', 'evidence', 'additionally', 'clearly', 'investigate', 'framework'];

    const wordSwapInterval = setInterval(() => {
      setLogPhase(prev => (prev + 1) % wordsToEliminate.length);
    }, 2800);

    return () => clearInterval(wordSwapInterval);
  }, []);

  return (
    <div className="flex flex-col h-full w-full">
      {/* Loading Status Indicator */}
      <div className="flex items-center gap-3 mb-8 pb-4 border-b border-zinc-100 dark:border-zinc-800">
        <Loader2 className="w-5 h-5 text-zinc-400 dark:text-zinc-500 animate-spin" />
        <div className="flex flex-col">
          <span className="text-zinc-900 dark:text-white font-medium text-[14px]">
            {stealthMode || isHumanizing ? "Humanizing draft..." : "Synthesizing content..."}
          </span>
          <span className="text-zinc-500 dark:text-zinc-400 text-[12px]">
            {PRESET_MESSAGES[activeStep].text}
          </span>
        </div>
      </div>

      {/* Output-Like Skeleton Loader */}
      <div className="flex-1 w-full space-y-6 animate-pulse">
        {/* Title Skeleton */}
        <div className="space-y-3">
          <div className="h-6 w-3/4 max-w-lg bg-zinc-100 dark:bg-zinc-800/50 rounded-sm"></div>
          <div className="h-4 w-1/4 max-w-sm bg-zinc-100 dark:bg-zinc-800/50 rounded-sm"></div>
        </div>

        {/* Paragraph 1 */}
        <div className="space-y-2.5">
          <div className="h-4 w-full bg-zinc-100 dark:bg-zinc-800/50 rounded-sm"></div>
          <div className="h-4 w-full bg-zinc-100 dark:bg-zinc-800/50 rounded-sm"></div>
          <div className="h-4 w-11/12 bg-zinc-100 dark:bg-zinc-800/50 rounded-sm"></div>
          <div className="h-4 w-5/6 bg-zinc-100 dark:bg-zinc-800/50 rounded-sm"></div>
        </div>

        {/* Paragraph 2 */}
        <div className="space-y-2.5 pt-2">
          <div className="h-4 w-full bg-zinc-100 dark:bg-zinc-800/50 rounded-sm"></div>
          <div className="h-4 w-10/12 bg-zinc-100 dark:bg-zinc-800/50 rounded-sm"></div>
          <div className="h-4 w-full bg-zinc-100 dark:bg-zinc-800/50 rounded-sm"></div>
        </div>

        {/* Paragraph 3 */}
        <div className="space-y-2.5 pt-2">
          <div className="h-4 w-11/12 bg-zinc-100 dark:bg-zinc-800/50 rounded-sm"></div>
          <div className="h-4 w-4/5 bg-zinc-100 dark:bg-zinc-800/50 rounded-sm"></div>
        </div>
      </div>
    </div>
  );
};
